import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import { confirmAlert } from "react-confirm-alert";
import { useSelector } from "react-redux";
import { deleteById, findByMuralCodigo, save } from "./api-mural-foto";
import { ADMIN, COORDENADOR, PROFESSOR } from "../../Usuario/Perfil";
import { getBase64 } from "../../../util/Base64";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { errorToast, success } from "../../../util/ToastUtil";
import Loading from "../../UI/Loading";
import MuralFotoLightbox from "./MuralFotoLightbox";
import MuralFotoList from "./MuralFotoList";

const MAX_FILE_SIZE = 10000000;

const EMPTY_PAGE = { content: [], number: -1, totalPages: 0 };

const MuralFotoContainer = ({ mural }) => {
  const usuario = useSelector((state) => state.usuario);
  const podeGerenciar = [ADMIN, COORDENADOR, PROFESSOR].includes(usuario?.perfil);
  const [muralFotos, setMuralFotos] = useState(EMPTY_PAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingCodigo, setDeletingCodigo] = useState(null);
  const [fotoIndex, setFotoIndex] = useState(null);
  const [isSentinelVisible, setIsSentinelVisible] = useState(false);
  const hiddenFileInput = useRef(null);
  const sentinelRef = useRef(null);
  const codMural = mural.codigo;

  const loadPage = useCallback(
    (pageNumber) => {
      setIsLoading(true);
      return findByMuralCodigo(codMural, pageNumber)
        .then((data) => {
          if (!data) return;
          if (data.error) {
            errorMessage(data.message);
            return;
          }
          setMuralFotos((prevState) => {
            const conhecidas = new Set(prevState.content.map((mf) => mf.codigo));
            return {
              number: data.number,
              totalPages: data.totalPages,
              content: [...prevState.content, ...data.content.filter((mf) => !conhecidas.has(mf.codigo))],
            };
          });
        })
        .finally(() => setIsLoading(false));
    },
    [codMural],
  );

  useEffect(() => {
    setMuralFotos(EMPTY_PAGE);
    loadPage(0);
  }, [loadPage]);

  const hasMore = muralFotos.number + 1 < muralFotos.totalPages;
  const total = muralFotos.content.length;

  // Observa o fim da grade para carregar mais fotos automaticamente.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsSentinelVisible(entry.isIntersecting), {
      rootMargin: "400px",
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, total]);

  // Continua carregando enquanto o fim da grade estiver visível, ou quando o
  // visualizador estiver perto da última foto carregada.
  const precisaDeMais = isSentinelVisible || (fotoIndex !== null && fotoIndex >= total - 2);
  useEffect(() => {
    if (precisaDeMais && hasMore && !isLoading) {
      loadPage(muralFotos.number + 1);
    }
  }, [precisaDeMais, hasMore, isLoading, loadPage, muralFotos.number]);

  const handleChangeImage = async (event) => {
    const files = Array.from(event.target.files || []);
    // Limpa o input para permitir enviar o mesmo arquivo novamente.
    event.target.value = "";
    if (files.length === 0) return;

    const grandes = files.filter((file) => file.size > MAX_FILE_SIZE);
    const validos = files.filter((file) => file.size <= MAX_FILE_SIZE);
    if (grandes.length > 0) {
      errorToast(`Aceito apenas arquivos até 10Mb. Ignorado(s): ${grandes.map((f) => f.name).join(", ")}.`);
    }
    if (validos.length === 0) return;

    setIsUploading(true);
    let enviadas = 0;
    // Envia uma de cada vez para não sobrecarregar o servidor com imagens em base64.
    for (const file of validos) {
      const foto = await getBase64(file);
      const data = await save({
        foto,
        mural,
        fileType: file.type,
        fileName: file.name,
        fileSize: file.size,
      });
      if (!data || data.error) {
        errorMessage(data?.message || `Não foi possível enviar ${file.name}.`);
        continue;
      }
      enviadas += 1;
      setMuralFotos((prevState) => ({ ...prevState, content: [...prevState.content, data] }));
    }
    setIsUploading(false);
    if (enviadas > 0) success(`${enviadas} foto(s) adicionada(s).`);
  };

  const deleteFoto = (muralFoto) => {
    setDeletingCodigo(muralFoto.codigo);
    deleteById(muralFoto.codigo)
      .then((data) => {
        if (data?.error) {
          errorMessage(data.message);
          return;
        }
        setMuralFotos((prevState) => ({
          ...prevState,
          content: prevState.content.filter((mf) => mf.codigo !== muralFoto.codigo),
        }));
        success("Foto excluída.");
      })
      .finally(() => setDeletingCodigo(null));
  };

  const handleDeleteFoto = (muralFoto) => {
    confirmAlert({
      title: "Alerta",
      message: "Deseja excluir esta foto?",
      buttons: [
        { label: "Sim", onClick: () => deleteFoto(muralFoto) },
        { label: "Não", onClick: () => {} },
      ],
    });
  };

  return (
    <Box width="100%">
      <MuralFotoLightbox
        fotos={muralFotos.content}
        index={fotoIndex}
        onClose={() => setFotoIndex(null)}
        onChangeIndex={setFotoIndex}
      />

      {podeGerenciar && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={2}>
          <Typography variant="body2" color="text.secondary">
            {total > 0 ? `${total} foto(s)${hasMore ? " carregada(s)" : ""}` : ""}
          </Typography>
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            ref={hiddenFileInput}
            onChange={handleChangeImage}
          />
          <Button
            variant="contained"
            startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <AddPhotoAlternateOutlinedIcon />}
            disabled={isUploading}
            onClick={() => hiddenFileInput.current.click()}
          >
            {isUploading ? "Enviando..." : "Adicionar Fotos"}
          </Button>
        </Stack>
      )}

      {total === 0 && isLoading ? (
        <Box py={6}>
          <Loading description="Carregando fotos..." />
        </Box>
      ) : total === 0 ? (
        <Box
          sx={{
            py: 6,
            textAlign: "center",
            color: "text.secondary",
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <PhotoLibraryOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1}>
            Nenhuma foto neste mural.
          </Typography>
        </Box>
      ) : (
        <>
          <MuralFotoList
            muralFotos={muralFotos.content}
            podeGerenciar={podeGerenciar}
            handleOpenFoto={setFotoIndex}
            handleDeleteFoto={handleDeleteFoto}
            deletingCodigo={deletingCodigo}
          />
          {hasMore && (
            <Box ref={sentinelRef} py={3}>
              <Loading size={24} description="Carregando mais fotos..." />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MuralFotoContainer;
